import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { PackageService } from '../../../api/packageService';
import { MenuService } from '../../../api/menuService';
import { PackageResponse, UnassignedMenu } from '../../../@type/package.types';
import { MenuItemResponse } from '../../../@type/menu.types';
import { useBreadcrumbLabel } from "../../../components/breadcrumb/BreadcrumbContext";

type AssignableMenu = {
  id: number;
  code: string;
  name: string;
  route_name?: string;
  sort_order?: number;
  is_active?: boolean;
  is_enabled?: boolean;
};

const PackageMenuConfig: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [packageInfo, setPackageInfo] = useState<PackageResponse | null>(null);
  useBreadcrumbLabel(packageInfo?.name, 1);
  const [allMenus, setAllMenus] = useState<AssignableMenu[]>([]);
  const [selectedMenuIds, setSelectedMenuIds] = useState<number[]>([]);

  const translate = (key: string, defaultValue: string, options?: Record<string, unknown>) =>
    t(key, { defaultValue, ...options });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [packageRes, menusRes, assignedRes, unassignedRes] = await Promise.all([
        PackageService.getPackageById(Number(id)),
        MenuService.getAllMenuItems({ page: 0, size: 100 }),
        PackageService.getPackageMenus(Number(id)),
        PackageService.getUnassignedMenus(),
      ]);

      setPackageInfo(packageRes.data.data || packageRes.data);

      const assignedMenus = getAssignedMenus(assignedRes.data.data || assignedRes.data);
      const menuItems = getMenuList(menusRes.data);
      const unassignedMenus = getUnassignedMenus(unassignedRes.data);
      const mergedMenus = mergeMenus(menuItems, assignedMenus, unassignedMenus);

      setAllMenus(mergedMenus);
      setSelectedMenuIds(assignedMenus.map((menu) => menu.id));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAssignedMenus = (data: any): AssignableMenu[] => {
    return (data?.assigned_menus || data?.menu_items || []).map((menu: any) => ({
      id: Number(menu.id),
      code: menu.code,
      name: menu.name || menu.code,
      is_enabled: menu.is_enabled,
    }));
  };

  const getMenuList = (data: any): AssignableMenu[] => {
    const menus: MenuItemResponse[] = data?.data?.content || data?.content || data?.data || [];

    return menus.map((menu) => ({
      id: Number(menu.id),
      code: menu.code,
      name: getLocalizedMenuName(menu),
      route_name: menu.route_name,
      sort_order: menu.sort_order,
      is_active: menu.is_active,
    }));
  };

  const getLocalizedMenuName = (menu: MenuItemResponse) => {
    const language = i18n.resolvedLanguage || i18n.language || 'en';
    const baseLanguage = language.split('-')[0];

    return (
      menu.translations?.[language]?.name ||
      menu.translations?.[baseLanguage]?.name ||
      menu.translations?.en?.name ||
      menu.code
    );
  };

  const getUnassignedMenus = (data: any): AssignableMenu[] => {
    const menus: UnassignedMenu[] = Array.isArray(data?.data) ? data.data : data || [];

    return menus.map((menu) => ({
      id: Number(menu.id),
      code: menu.code,
      name: menu.name || menu.code,
      is_active: menu.is_active,
    }));
  };

  const mergeMenus = (...menuGroups: AssignableMenu[][]): AssignableMenu[] => {
    const menusById = new Map<number, AssignableMenu>();

    menuGroups.flat().forEach((menu) => {
      const existing = menusById.get(menu.id);
      menusById.set(menu.id, {
        ...existing,
        ...menu,
        name: menu.name && menu.name !== menu.code ? menu.name : existing?.name || menu.name || menu.code,
      });
    });

    return Array.from(menusById.values()).sort((a, b) => {
      const orderA = a.sort_order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.sort_order ?? Number.MAX_SAFE_INTEGER;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return a.code.localeCompare(b.code);
    });
  };

  const handleToggle = (menuId: number) => {
    setSelectedMenuIds((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await PackageService.assignMenusToPackage(Number(id), selectedMenuIds);
      navigate('/settings/packages');
    } catch (error) {
      console.error('Failed to save menu assignment:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderMenuList = (menus: AssignableMenu[], emptyText: string) => {
    if (menus.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        </Box>
      );
    }

    return (
      <List dense className="border rounded">
        {menus.map((menu) => {
          const isSelected = selectedMenuIds.includes(menu.id);

          return (
            <ListItem key={menu.id} disablePadding divider>
              <ListItemButton onClick={() => handleToggle(menu.id)} dense disabled={loading}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={isSelected}
                    tabIndex={-1}
                    disableRipple
                    disabled={loading}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={menu.name || menu.code}
                  secondary={[
                    `${translate('packages.menuCodeLabel', 'Code')}: ${menu.code}`,
                    menu.route_name ? `${translate('packages.routeLabel', 'Route')}: ${menu.route_name}` : null,
                    menu.sort_order !== undefined
                      ? `${translate('packages.sortOrderLabel', 'Sort Order')}: ${menu.sort_order}`
                      : null,
                  ].filter(Boolean).join(' | ')}
                  className="capitalize"
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    );
  };

  // Menu bị deactive không hiển thị để gán; id đã gán vẫn nằm trong selectedMenuIds nên không bị mất khi lưu
  const visibleMenus = allMenus.filter((menu) => menu.is_active !== false);
  const assignedMenus = visibleMenus.filter((menu) => selectedMenuIds.includes(menu.id));
  const unassignedMenus = visibleMenus.filter((menu) => !selectedMenuIds.includes(menu.id));

  if (loading && !packageInfo) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Typography variant="h4" className="font-bold">
          {t('packages.menuAssignment')}
        </Typography>
        {packageInfo && (
          <Typography
            variant="subtitle1"
            className="mt-2"
            sx={{ color: 'var(--app-text-muted)' }}
          >
            {packageInfo.name}
          </Typography>
        )}
      </div>

      <Paper className="p-6 max-w-4xl">
        <Alert severity="info" className="mb-4">
          {translate(
            'packages.menuConfigInstruction',
            'Check menus to assign them to this package. Uncheck an assigned menu to unassign it when saving.'
          )}
        </Alert>

        <Box display="grid" gap={3}>
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="h6" className="font-semibold">
                {translate('packages.assignedMenus', 'Assigned menus')}
              </Typography>
              <Chip
                label={translate('packages.selectedCount', '{{count}} selected', { count: assignedMenus.length })}
                color="primary"
                size="small"
              />
            </Box>
            {renderMenuList(assignedMenus, translate('packages.noMenusAssigned', 'No menus assigned'))}
          </Box>

          <Divider />

          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="h6" className="font-semibold">
                {translate('packages.unassignedMenus', 'Unassigned menus')}
              </Typography>
              <Chip label={`${unassignedMenus.length}`} size="small" />
            </Box>
            {renderMenuList(unassignedMenus, translate('packages.allMenusAssigned', 'All menus are assigned'))}
          </Box>
        </Box>

        <div className="flex gap-4 justify-end mt-6">
          <Button
            variant="outlined"
            onClick={() => navigate('/settings/packages')}
            disabled={loading}
          >
            {translate('btn.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
            className="bg-buttton"
            sx={{ textTransform: 'capitalize' }}
          >
            {loading ? <CircularProgress size={24} /> : translate('btn.save', 'Save')}
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default PackageMenuConfig;
